import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";

import infoPages from "./data/infoPages.json";

export default function InfoPage() {
  const { page } = useParams();
  const pageData = infoPages[page];

  // ✅ Track which FAQ is expanded
  const [openIndex, setOpenIndex] = useState(null);

  if (!pageData) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
        <p className="text-gray-700">
          The page you are looking for does not exist.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">{pageData.title}</h1>

      {/* ✅ FAQ Section with Accordion */}
      {Array.isArray(pageData.content) ? (
        <div className="space-y-4">
          {pageData.content.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="border-b border-gray-200 pb-2 cursor-pointer"
              >
                <div
                  className="flex justify-between items-center"
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                >
                  <h3 className="font-semibold text-lg">{item.question}</h3>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                {isOpen && (
                  <p className="mt-2 text-gray-700 leading-relaxed pl-2">
                    {item.answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ✅ Normal Content Section */
        <div className="text-gray-700 leading-relaxed space-y-3">
          {pageData.content.split("\n").map((line, idx) => {
            const trimmed = line.trim();
            if (!trimmed) return null; // skip empty lines

            // ✅ Bold Headings like **Our Mission**
            if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
              const heading = trimmed.replace(/\*\*/g, "");
              return (
                <h2 key={idx} className="text-xl font-bold mt-4">
                  {heading}
                </h2>
              );
            }

            // ✅ Detect email line starting with 📧
            if (trimmed.startsWith("📧")) {
              const email = trimmed.split(":")[1]?.trim();
              return (
                <p key={idx}>
                  📧 Email Us:{" "}
                  <a
                    href={`mailto:${email}`}
                    className="text-blue-600 underline"
                  >
                    {email}
                  </a>
                </p>
              );
            }

            // ✅ Detect website line starting with 🌐
            if (trimmed.startsWith("🌐")) {
              const url = trimmed.split(":")[1]?.trim();
              return (
                <p key={idx}>
                  🌐 Website:{" "}
                  <a
                    href={`https://${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {url}
                  </a>
                </p>
              );
            }

            return <p key={idx}>{trimmed}</p>;
          })}
        </div>
      )}
    </div>
  );
}
